import { useEffect, useMemo, useRef, useState, type ChangeEvent, type MouseEvent } from "react";
import { Check, ChevronDown, MoreVertical, Search, ShieldCheck, X } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { snippetsNav, snippetsNavAnchors } from "@/mocks/snippets";
import { goAnchor } from "./app-nav";
import { locale, t, tn } from "@/lib/i18n";
import { snippetsApi, type Snippet as ApiSnippet, type SnippetFile } from "./snippets-api";
import { apiErrorText } from "@/lib/api";

/* Живой макет «Шаблоны»: сохранённые ответы с коротким ID для вызова через «/».
   Всё работает в макете — создание, правка, дублирование, файлы и удаление шаблонов;
   отправленные сообщения и настоящие диалоги не затрагиваются. */

/* Экран на API: шаблоны хранятся на сервере (`/snippets`), файлы загружаются заранее и прикладываются к шаблону.
   Правят владелец, администраторы и менеджеры (canEdit с сервера); остальные видят список и копируют /ключ. */
type Snippet = ApiSnippet;
type Draft = { id: string | null; name: string; fieldId: string; message: string; topic: string; files: SnippetFile[] };

const EMPTY_DRAFT: Draft = { id: null, name: "", fieldId: "", message: "", topic: "", files: [] };
const MAX_FILE = 10 * 1024 * 1024;
const FIELD =
  "h-10 w-full box-border min-w-0 rounded-btn border border-border bg-background px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25";

const formatDate = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
};

// «Приветствие» → privetstvie (тот же транслит, что в полях контактов)
const TRANSLIT: Record<string, string> = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m",
  н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "ts", ч: "ch", ш: "sh", щ: "sch",
  ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
};

const slug = (v: string) => {
  const words = v.trim().toLowerCase().split(/[^a-zа-яё0-9]+/i).filter(Boolean);
  const joined = words.map((w) => Array.from(w).map((c) => TRANSLIT[c] ?? c).join("")).join("");
  const clean = joined.replace(/[^a-z0-9]/g, "").slice(0, 24);
  return /^[a-z]/.test(clean) ? clean : clean ? `s${clean}` : "";
};

function SnippetsDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const [items, setItems] = useState<Snippet[]>([]);
  const [topics, setTopics] = useState<string[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [topic, setTopic] = useState("");
  const [menu, setMenu] = useState<{ id: string; top: number; left: number } | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [idTouched, setIdTouched] = useState(false);
  const [toast, setToast] = useState("");
  const fileInput = useRef<HTMLInputElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const load = async () => {
    try {
      const r = await snippetsApi.list();
      setItems(r.items);
      setTopics(r.topics);
      setCanEdit(r.canEdit);
    } catch (err) {
      flash(apiErrorText(err));
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if ((e.target as HTMLElement | null)?.closest?.("[data-menu-root]")) return;
      setMenu(null);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, []);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [menu]);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4200);
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((s) => {
      if (topic && s.topic !== topic) return false;
      if (!q) return true;
      return `${s.name} ${s.key} ${s.message} ${t(s.topic)} ${s.files.map((f) => f.name).join(" ")}`.toLowerCase().includes(q);
    });
  }, [items, query, topic]);

  const openAdd = () => {
    setDraft({ ...EMPTY_DRAFT, files: [] });
    setIdTouched(false);
    setMenu(null);
  };

  const openEdit = (s: Snippet) => {
    setDraft({ id: s.id, name: s.name, fieldId: s.key, message: s.message, topic: s.topic, files: [...s.files] });
    setIdTouched(true);
    setMenu(null);
  };

  const idError = (() => {
    if (!draft) return "";
    const id = draft.fieldId.trim();
    if (!id) return "";
    if (!/^[a-z][a-z0-9_-]{1,29}$/.test(id)) return t("Латинские буквы и цифры, без пробелов: greeting, price-2026");
    if (items.some((s) => s.key === id && s.id !== draft.id)) return t("Такой ID уже занят — выберите другой");
    return "";
  })();

  const messageLen = draft ? draft.message.trim().length : 0;
  const canSave = Boolean(
    draft &&
      !busy &&
      draft.name.trim().length >= 2 &&
      /^[a-z][a-z0-9_-]{1,29}$/.test(draft.fieldId.trim()) &&
      !idError &&
      (messageLen >= 3 || draft.files.length > 0),
  );

  const save = async () => {
    if (!draft || !canSave) return;
    const data = { name: draft.name.trim(), key: draft.fieldId.trim(), message: draft.message.trim(), topic: draft.topic, files: draft.files.map((f) => f.id) };
    setBusy(true);
    try {
      if (draft.id) {
        const saved = await snippetsApi.update(draft.id, data);
        setItems((prev) => prev.map((s) => (s.id === saved.id ? saved : s)));
        flash(t("Шаблон «{name}» обновлён — в диалогах он уже доступен по /{id}", { name: saved.name, id: saved.key }));
      } else {
        const saved = await snippetsApi.create(data);
        setItems((prev) => [saved, ...prev]);
        flash(t("Шаблон «{name}» создан — наберите /{id} в поле ответа", { name: saved.name, id: saved.key }));
      }
      setDraft(null);
    } catch (err) {
      flash(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async (s: Snippet) => {
    let n = 2;
    let id = `${s.key}${n}`;
    while (items.some((x) => x.key === id)) {
      n += 1;
      id = `${s.key}${n}`;
    }
    setMenu(null);
    try {
      const copy = await snippetsApi.create({ name: t("{name} (копия)", { name: s.name }).slice(0, 60), key: id, message: s.message, topic: s.topic, files: s.files.map((f) => f.id) });
      setItems((prev) => {
        const i = prev.findIndex((x) => x.id === s.id);
        const next = [...prev];
        next.splice(i + 1, 0, copy);
        return next;
      });
      flash(t("Копия шаблона создана — вызывается по /{id}", { id }));
    } catch (err) {
      flash(apiErrorText(err));
    }
  };

  const remove = async (s: Snippet) => {
    setMenu(null);
    try {
      await snippetsApi.remove(s.id);
      setItems((prev) => prev.filter((x) => x.id !== s.id));
      flash(t("Шаблон «{name}» удалён — отправленные сообщения остаются в диалогах", { name: s.name }));
    } catch (err) {
      flash(apiErrorText(err));
    }
  };

  const insert = (s: Snippet) => {
    setMenu(null);
    copyId(s);
    goAnchor("#inbox-app");
  };

  const copyId = (s: Snippet) => {
    navigator.clipboard?.writeText(`/${s.key}`).catch(() => undefined);
    flash(t("Сокращение /{id} скопировано — вставьте его в поле ответа", { id: s.key }));
  };

  const addFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (picked.length === 0 || !draft) return;
    const room = 5 - draft.files.length;
    if (room <= 0) {
      flash(t("До 5 файлов, каждый до 10 МБ — они приложатся к сообщению вместе с текстом."));
      return;
    }
    setBusy(true);
    try {
      for (const f of picked.slice(0, room)) {
        if (f.size > MAX_FILE) {
          flash(t("Файл «{name}» больше 10 МБ.", { name: f.name }));
          continue;
        }
        const up = await snippetsApi.upload(f);
        setDraft((d) => d && { ...d, files: [...d.files, { ...up, url: "" }] });
      }
    } catch (err) {
      flash(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const openRowMenu = (e: MouseEvent<HTMLButtonElement>, s: Snippet) => {
    const r = e.currentTarget.getBoundingClientRect();
    const width = 200;
    setMenu({
      id: s.id,
      top: Math.min(r.bottom + 4, window.innerHeight - 220),
      left: Math.max(8, Math.min(window.innerWidth - width - 8, r.right - width)),
    });
  };

  const onNav = (id: string) => {
    if (id === "snippets") return;
    const anchor = snippetsNavAnchors[id];
    if (anchor) {
      goAnchor(anchor);
      return;
    }
    flash(t("Раздел «{name}» в демо не открывается", { name: snippetsNav.flatMap((g) => g.items).find((x) => x.id === id)?.label ?? "" }));
  };

  const th = "py-2.5 pe-3 text-start font-normal align-middle last:pe-0";
  const rowPad = "py-2.5";
  const menuSnippet = menu ? items.find((s) => s.id === menu.id) : null;

  return (
    <Band id="snippets" tone="default">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} / {t("настройки → входящие → шаблоны")}
          </span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {t("Шаблонов: {n}", { n: items.length })}
          </span>
        </div>

        <div className="grid lg:grid-cols-[14.5rem_minmax(0,1fr)]">
          {/* ── настройки ── */}
          <div className="hidden flex-col gap-4 border-border p-3 lg:flex lg:border-e">
            {snippetsNav.map((g) => (
              <div key={g.group} className="flex flex-col gap-0.5">
                <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{g.group}</p>
                {g.items.map((it) => {
                  const active = it.id === "snippets";
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onNav(it.id)}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[12.5px] font-medium transition",
                        active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <i className={cn(it.icon, "text-base leading-none")} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{it.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}

            <div className="mt-auto rounded-btn border border-border bg-background/60 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <ShieldCheck className="size-3.5" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("Шаблоны меняет администратор. Продавцы используют их в ответах, но не редактируют.")}
              </p>
            </div>
          </div>

          {/* ── список шаблонов ── */}
          <div className="min-w-0 p-3.5 md:p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold leading-snug">{t("Шаблоны ответов")}</h3>
                <p className="mt-1 max-w-xl text-[12px] leading-relaxed text-muted-foreground">
                  {t("Сохранённые ответы с текстом и файлами. Наберите «/» и ID шаблона в поле ответа, чтобы вставить его в диалог.")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => flash(t("В поле ответа наберите «/» — появится список шаблонов; подстановки $contact.firstName и $user.name заменятся данными при отправке."))}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[12px] font-semibold text-primary transition hover:opacity-80"
              >
                <i className="ri-book-open-line text-sm leading-none" aria-hidden />
                {t("Узнать больше")}
              </button>
            </div>

            <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1 lg:hidden">
              {snippetsNav.flatMap((g) => g.items).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onNav(it.id)}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    it.id === "snippets" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  <i className={it.icon} aria-hidden />
                  {it.label}
                </button>
              ))}
            </div>

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              {canEdit ? (
                <button
                  type="button"
                  onClick={openAdd}
                  className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
                >
                  <i className="ri-add-line" aria-hidden />
                  {t("Добавить шаблон")}
                </button>
              ) : null}
              <label className="relative block min-w-0 sm:ms-auto sm:w-60">
                <span className="sr-only">{t("Поиск по шаблонам")}</span>
                <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("Найти шаблон: приветствие, счёт…")}
                  className="h-9 w-full box-border rounded-btn border border-border bg-background ps-9 pe-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                />
              </label>
              <label className="relative inline-flex min-w-0 items-center">
                <span className="sr-only">{t("Фильтр по теме")}</span>
                <select
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className="h-9 w-full cursor-pointer appearance-none rounded-btn border border-border bg-background ps-3 pe-8 text-[12px] font-medium text-foreground focus:border-primary focus:outline-none sm:w-auto"
                >
                  <option value="">{t("Все темы")}</option>
                  {topics.map((tp) => (
                    <option key={tp} value={tp}>
                      {t(tp)}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute end-2.5 size-3.5 text-muted-foreground" aria-hidden />
              </label>
            </div>

            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-start">
                <thead>
                  <tr className="border-b border-border">
                    <th scope="col" className={th}>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Название")}</span>
                    </th>
                    <th scope="col" className={cn(th, "hidden sm:table-cell")}>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">ID</span>
                    </th>
                    <th scope="col" className={cn(th, "hidden lg:table-cell")}>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Сообщение")}</span>
                    </th>
                    <th scope="col" className={th}>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Тема")}</span>
                    </th>
                    <th scope="col" className={cn(th, "hidden sm:table-cell")}>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Файлы")}</span>
                    </th>
                    <th scope="col" className={cn(th, "hidden md:table-cell")}>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Обновлён")}</span>
                    </th>
                    <th scope="col" className="w-10 py-2.5 text-end">
                      <span className="sr-only">{t("Действия")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((s) => (
                    <tr key={s.id} className="border-b border-border/60 transition last:border-0 hover:bg-muted/40">
                      <td className={cn(rowPad, "pe-3")}>
                        <button
                          type="button"
                          onClick={() => (canEdit ? openEdit(s) : copyId(s))}
                          className="cursor-pointer text-start text-[13px] font-semibold leading-snug hover:text-primary"
                        >
                          {s.name}
                        </button>
                        <p className="mt-0.5 line-clamp-1 max-w-[15rem] text-[11px] text-muted-foreground sm:hidden">/{s.key}</p>
                      </td>
                      <td className={cn(rowPad, "hidden pe-3 sm:table-cell")}>
                        <button
                          type="button"
                          onClick={() => copyId(s)}
                          title={t("Скопировать сокращение")}
                          className="flash-mono cursor-pointer rounded-full border border-border bg-background/70 px-2 py-0.5 text-[11px] text-muted-foreground transition hover:border-primary/50 hover:text-primary"
                        >
                          /{s.key}
                        </button>
                      </td>
                      <td className={cn(rowPad, "hidden pe-4 lg:table-cell")}>
                        <span className="line-clamp-1 max-w-[20rem] text-[12px] text-muted-foreground">{s.message}</span>
                      </td>
                      <td className={cn(rowPad, "pe-3")}>
                        {s.topic ? (
                          <span
                            className={cn(
                              "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium",
                              s.topic === "Оплата" || s.topic === "Доставка"
                                ? "border-amber-400/45 bg-amber-400/12 text-amber-600"
                                : "border-primary/35 bg-primary/10 text-primary",
                            )}
                          >
                            <i className="ri-price-tag-3-line text-[11px] leading-none" aria-hidden />
                            {t(s.topic)}
                          </span>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">{t("Без темы")}</span>
                        )}
                      </td>
                      <td className={cn(rowPad, "hidden pe-3 sm:table-cell")}>
                        {s.files.length > 0 ? (
                          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] text-muted-foreground">
                            <i className="ri-attachment-2 text-[14px] leading-none" aria-hidden />
                            {s.files.length}
                          </span>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className={cn(rowPad, "hidden whitespace-nowrap pe-3 text-[12px] tabular-nums text-muted-foreground md:table-cell")}>
                        {formatDate(s.updatedAt)}
                      </td>
                      <td className={cn(rowPad, "text-end")}>
                        <div className="relative inline-block" data-menu-root>
                          <button
                            type="button"
                            onClick={(e) => (menu?.id === s.id ? setMenu(null) : openRowMenu(e, s))}
                            aria-label={t("Действия с шаблоном {name}", { name: s.name })}
                            aria-expanded={menu?.id === s.id}
                            className="grid size-7 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
                          >
                            <MoreVertical className="size-4" aria-hidden />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {visible.length === 0 ? (
                <div className="py-12 text-center">
                  <span className="mx-auto grid size-12 place-items-center rounded-btn bg-primary/10 text-2xl leading-none text-primary" aria-hidden>
                    <i className="ri-file-copy-2-line" />
                  </span>
                  <p className="mt-3 text-sm font-semibold">{t("Шаблонов не найдено")}</p>
                  <p className="mx-auto mt-1 max-w-sm text-[12px] leading-relaxed text-muted-foreground">
                    {t("Измените запрос или тему, а если шаблона ещё нет — создайте его: сохранённые ответы экономят время на частых вопросах.")}
                  </p>
                  {canEdit ? (
                    <button
                      type="button"
                      onClick={openAdd}
                      className="mt-3 inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
                    >
                      <i className="ri-add-line" aria-hidden />
                      {t("Создать шаблон")}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
              <span>
                {tn(visible.length, "шаблон", "шаблона", "шаблонов")}
                {topic ? ` · ${t("тема «{topic}»", { topic: t(topic) })}` : ""}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-flashlight-line text-[13px] leading-none text-primary" aria-hidden />
                {t("Вызов в диалоге: /ID")}
              </span>
            </div>

          </div>
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}

        {/* ── меню действий над шаблоном ── */}
        {menu && menuSnippet ? (
          <div
            role="menu"
            data-menu-root
            style={{ top: menu.top, left: menu.left }}
            className="fixed z-50 w-[200px] overflow-hidden rounded-btn border border-border bg-card p-1 shadow-lift"
          >
            {canEdit ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => openEdit(menuSnippet)}
                className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium transition hover:bg-muted"
              >
                <i className="ri-pencil-line text-[14px] leading-none text-muted-foreground" aria-hidden />
                {t("Изменить шаблон")}
              </button>
            ) : null}
            <button
              type="button"
              role="menuitem"
              onClick={() => insert(menuSnippet)}
              className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium transition hover:bg-muted"
            >
              <i className="ri-send-plane-line text-[14px] leading-none text-muted-foreground" aria-hidden />
              {t("Вставить в диалог")}
            </button>
            {canEdit ? (
              <>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => void duplicate(menuSnippet)}
                  className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium transition hover:bg-muted"
                >
                  <i className="ri-file-copy-2-line text-[14px] leading-none text-muted-foreground" aria-hidden />
                  {t("Дублировать")}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => void remove(menuSnippet)}
                  className="flex w-full cursor-pointer items-center gap-2 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium text-red-500 transition hover:bg-red-500/10"
                >
                  <i className="ri-delete-bin-line text-[14px] leading-none" aria-hidden />
                  {t("Удалить")}
                </button>
              </>
            ) : null}
          </div>
        ) : null}

        {/* ── окно создания / правки шаблона ── */}
        {draft ? (
          <div className="absolute inset-0 z-[60] flex items-end justify-center bg-black/65 p-3 backdrop-blur-sm sm:items-center md:p-6">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="snippet-dialog-title"
              className="relative max-h-full w-full max-w-lg overflow-y-auto rounded-card border border-border bg-card p-5 shadow-lift md:p-6"
            >
              <button
                type="button"
                onClick={() => setDraft(null)}
                aria-label={t("Закрыть окно шаблона")}
                className="absolute end-3 top-3 grid size-8 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" aria-hidden />
              </button>

              <div className="flex items-start gap-3 pe-8">
                <span className="grid size-11 flex-none place-items-center rounded-btn bg-primary/10 text-2xl leading-none text-primary" aria-hidden>
                  <i className="ri-file-copy-2-line" />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    {draft.id ? t("Настройка шаблона") : t("Новый шаблон")}
                  </p>
                  <h3 id="snippet-dialog-title" className="mt-1 text-lg font-semibold leading-snug">
                    {draft.id ? t("Шаблон «{name}»", { name: draft.name || t("без названия") }) : t("Создать шаблон")}
                  </h3>
                </div>
              </div>

              <p className="mt-3 text-[11.5px] leading-relaxed text-muted-foreground">
                {t("Шаблоны позволяют быстро отправлять сохранённые ответы с текстом и файлами. Наберите «/» и ID шаблона, чтобы вставить его.")}
              </p>

              <div className="mt-4 flex flex-col gap-3">
                <label className="flex min-w-0 flex-col gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Название")}</span>
                  <input
                    value={draft.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setDraft((d) => d && { ...d, name, fieldId: idTouched ? d.fieldId : slug(name) });
                    }}
                    placeholder={t("Например, Приветствие")}
                    autoComplete="off"
                    maxLength={60}
                    className={FIELD}
                  />
                </label>

                <label className="flex min-w-0 flex-col gap-1.5">
                  <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("ID шаблона")}
                    <i className="ri-information-line text-[13px] leading-none text-muted-foreground/70" aria-hidden />
                  </span>
                  <span className="relative flex items-center">
                    <span className="flash-mono pointer-events-none absolute start-3 text-[13px] text-muted-foreground" aria-hidden>
                      /
                    </span>
                    <input
                      value={draft.fieldId}
                      onChange={(e) => {
                        setIdTouched(true);
                        setDraft((d) => d && { ...d, fieldId: e.target.value.replace(/\s+/g, "").toLowerCase() });
                      }}
                      placeholder="greeting"
                      autoComplete="off"
                      spellCheck={false}
                      maxLength={30}
                      className={cn(FIELD, "flash-mono ps-6", idError && "border-red-400 focus:border-red-400 focus:ring-red-400/25")}
                    />
                  </span>
                </label>

                <label className="flex min-w-0 flex-col gap-1.5">
                  <span className="flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      {t("Сообщение")}
                      <i className="ri-information-line text-[13px] leading-none text-muted-foreground/70" aria-hidden />
                    </span>
                    <span className={cn("normal-case tracking-normal tabular-nums", messageLen > 900 && "text-red-500")}>{messageLen}/1000</span>
                  </span>
                  <textarea
                    value={draft.message}
                    onChange={(e) => setDraft((d) => d && { ...d, message: e.target.value.slice(0, 1000) })}
                    rows={4}
                    placeholder={t("Привет, $contact.firstName! Чем можем помочь?")}
                    className={cn(FIELD, "h-auto min-h-24 resize-y py-2.5 leading-relaxed")}
                  />
                  <span className="text-[10.5px] leading-relaxed text-muted-foreground">
                    {t("Подстановки:")} <code className="flash-mono">$contact.firstName</code>, <code className="flash-mono">$user.name</code> {t("— заменятся данными при отправке.")}
                  </span>
                </label>

                <label className="flex min-w-0 flex-col gap-1.5">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("Тема (необязательно)")}
                    <i className="ri-information-line text-[13px] leading-none text-muted-foreground/70" aria-hidden />
                  </span>
                  <span className="relative flex items-center">
                    <select
                      value={draft.topic}
                      onChange={(e) => setDraft((d) => d && { ...d, topic: e.target.value })}
                      className={cn(FIELD, "cursor-pointer appearance-none pe-8")}
                    >
                      <option value="">{t("Без темы")}</option>
                      {topics.map((tp) => (
                        <option key={tp} value={tp}>
                          {t(tp)}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute end-2.5 size-3.5 text-muted-foreground" aria-hidden />
                  </span>
                </label>

                <div className="flex min-w-0 flex-col gap-1.5">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("Файл (необязательно)")}
                    <i className="ri-information-line text-[13px] leading-none text-muted-foreground/70" aria-hidden />
                  </span>
                  <button
                    type="button"
                    onClick={() => fileInput.current?.click()}
                    className="inline-flex h-10 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn border border-border bg-background px-3 text-[12px] font-semibold text-muted-foreground transition hover:border-primary/60 hover:text-primary"
                  >
                    <i className="ri-add-line" aria-hidden />
                    {t("Добавить файл")}
                  </button>
                  <input ref={fileInput} type="file" multiple tabIndex={-1} aria-hidden onChange={(e) => void addFiles(e)} className="hidden" />
                  {draft.files.length > 0 ? (
                    <ul className="mt-1 flex flex-wrap gap-1.5">
                      {draft.files.map((f) => (
                        <li
                          key={f.id}
                          className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-background/70 ps-2.5 pe-1.5 py-1 text-[11px] font-medium text-foreground/85"
                        >
                          <i className="ri-attachment-2 text-[12px] leading-none text-muted-foreground" aria-hidden />
                          <span className="max-w-[12rem] truncate">{f.name}</span>
                          <button
                            type="button"
                            onClick={() => setDraft((d) => d && { ...d, files: d.files.filter((x) => x.id !== f.id) })}
                            aria-label={t("Убрать файл {name}", { name: f.name })}
                            className="grid size-4 cursor-pointer place-items-center rounded-full text-muted-foreground transition hover:bg-red-500/10 hover:text-red-500"
                          >
                            <X className="size-3" aria-hidden />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <span className="text-[10.5px] text-muted-foreground">{t("До 5 файлов, каждый до 10 МБ — они приложатся к сообщению вместе с текстом.")}</span>
                </div>
              </div>

              {idError ? <p className="mt-2 text-[11px] text-red-500">{idError}</p> : null}
              {!canSave && !idError ? (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  {draft.name.trim().length < 2
                    ? t("Добавьте название — по нему шаблон ищут в диалоге.")
                    : messageLen < 3 && draft.files.length === 0
                      ? t("Напишите текст сообщения или приложите файл — это то, что получит клиент.")
                      : ""}
                </p>
              ) : null}

              <p className="mt-3 flex items-start gap-2 rounded-btn border border-border bg-background/60 px-3 py-2.5 text-[11px] leading-relaxed text-muted-foreground">
                <ShieldCheck className="mt-0.5 size-3.5 flex-none text-primary" aria-hidden />
                {t("Шаблон доступен всем сотрудникам вашего рабочего пространства: в поле ответа наберите «/» и выберите его.")}
              </p>

              <div className="mt-4 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => setDraft(null)}
                  className="inline-flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border px-4 text-[13px] font-semibold text-muted-foreground transition hover:text-foreground"
                >
                  {t("Отмена")}
                </button>
                <button
                  type="submit"
                  disabled={!canSave}
                  className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Check className="size-4" aria-hidden />
                  {draft.id ? t("Сохранить") : t("Создать шаблон")}
                </button>
              </div>
            </form>
          </div>
        ) : null}
      </div>
    </Band>
  );
}

export default SnippetsDemo;